import { LeftOutlined, PrinterOutlined } from '@ant-design/icons'
import { Button, Card, Flex, Form, Space, Tag, Upload } from 'antd'
import { FormInstance } from 'antd/lib'
import dayjs from 'dayjs'
import { useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'

import {
  InternalInquiryDocumentResponse,
  InternalInventoryDocumentState,
  InternalIssuanceDocumentResponse,
  InventoryDocumentState
} from 'src/api/requests'
import { CustomItemRender, Text } from 'src/components'
import { getStatus } from 'src/features/stock/utils'
import {
  LocalAdminConsumableTable,
  LocalCommentSectionAdmin
} from 'src/features/stock-local/components/applications'
import { FileDocument, onDownloadFile } from 'src/hooks'
import { dayMonthYearHourMinutesFormatSeparator } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

type Props = {
  data?: InternalIssuanceDocumentResponse
  inquireData?: InternalInquiryDocumentResponse
  form: FormInstance
}

export const LocalStockIssuanceAcceptedDetails = ({
  data,
  inquireData,
  form
}: Props) => {
  const { t } = useLocales()
  const navigate = useNavigate()

  const { id } = useParams()

  const printableRef = useRef<HTMLDivElement>(null)

  const isRejected = data?.state === InternalInventoryDocumentState.REJECTED

  const onPrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: `Stock_Issuance_${id}`
  })

  const onBackClick = () => {
    navigate(paths.stockLocal.issuance.root)
  }

  return (
    <Flex vertical gap={24}>
      <Flex justify="space-between">
        <Button
          icon={<LeftOutlined />}
          className="fit-content"
          onClick={onBackClick}
          disabled={false}
        >
          {t('common.back')}
        </Button>

        <Space size={8}>
          <Button
            icon={<PrinterOutlined />}
            disabled={false}
            onClick={() => onPrint()}
          >
            {t('stock_applications.print')}
          </Button>
        </Space>
      </Flex>

      <Flex vertical gap={16} ref={printableRef}>
        <Card>
          <Flex vertical gap={16}>
            <Flex vertical gap={8}>
              <Flex justify="space-between">
                <Space>
                  <Text weight={400}>
                    {t('stock_applications.issuance_based_on_inquire')}:
                  </Text>
                  <Text strong>#{data?.code}</Text>
                </Space>
                <Tag
                  color={
                    getStatus(t, data?.state ?? InventoryDocumentState.NEW)
                      .color
                  }
                >
                  {getStatus(t, data?.state ?? InventoryDocumentState.NEW).text}
                </Tag>
              </Flex>

              <Space>
                <Text weight={400}>
                  {t('stock_applications.request_date')}:
                </Text>
                <Text strong>
                  {dayjs(data?.documentDate).format(
                    dayMonthYearHourMinutesFormatSeparator
                  )}
                </Text>
              </Space>

              {data?.processedOn && (
                <Space>
                  <Text weight={400}>
                    {isRejected
                      ? `${t('stock_applications.refusal_date')}:`
                      : `${t('stock_applications.accepted_date')}:`}
                  </Text>
                  <Text strong>
                    {dayjs(data?.processedOn).format(
                      dayMonthYearHourMinutesFormatSeparator
                    )}
                  </Text>
                </Space>
              )}

              <Space>
                <Text weight={400}>
                  {t('stock_applications.initiated_by')}:
                </Text>
                <Text strong>
                  {data?.initializedBy?.firstName}{' '}
                  {data?.initializedBy?.lastName} -
                </Text>
                <Text strong>{data?.role?.name}</Text>
              </Space>

              {data?.processedBy && (
                <Space>
                  <Text weight={400}>
                    {isRejected
                      ? `${t('stock_applications.refused_by')}:`
                      : `${t('stock_applications.accepted_by')}:`}
                  </Text>
                  <Text strong>
                    {data?.processedBy?.firstName} {data?.processedBy?.lastName}{' '}
                    -
                  </Text>
                  <Text strong>{data?.role?.name}</Text>
                </Space>
              )}
            </Flex>
          </Flex>
        </Card>

        <Card>
          <Flex vertical gap={16}>
            <LocalAdminConsumableTable
              isEdit={data?.state !== InternalInventoryDocumentState.REJECTED}
              data={data}
              form={form}
              isSubmitted={true}
            />

            <Card title={t('stock_applications.uploaded_documents')}>
              <Form.Item name="docs" className="full-width">
                <Upload
                  itemRender={(_, file: FileDocument) => (
                    <CustomItemRender
                      file={file}
                      onDownload={() => onDownloadFile(t, file?.id, file?.name)}
                    />
                  )}
                  fileList={inquireData?.mediaFiles?.map((file, index) => ({
                    ...file,
                    uid: String(index),
                    name: file?.name ?? `file-${index}`
                  }))}
                  disabled
                />
              </Form.Item>
            </Card>

            <LocalCommentSectionAdmin data={inquireData} issuanceData={data} />
          </Flex>
        </Card>
      </Flex>
    </Flex>
  )
}
